import { toString as urlToQrString } from "qrcode";
import {
    DesignInputStep,
    DigitalContentDesignInputStepData,
    FrameDesignInputStepData,
    IllustrationDesignInputStepData,
    ModuleDesignInputStepData,
    PictureDesignInputStepData,
    ShapeDesignInputStepData,
    TextDesignInputStepData,
} from "./types";
import moduleResolver from "../module/resolver";
import { LayoutNotFoundError, ParseError } from "../util/exception";
import { optionService } from "../services/option";
import {
    AnyStepData,
    ColorDefinition,
    DigitalContentStepData,
    FontData,
    FrameElement,
    FrameStepData,
    ILayout,
    IllustrationElement,
    IllustrationStepData,
    ImageElement,
    LayoutElement,
    LayoutElementType,
    ModuleStepData,
    PictureStepData,
    Region,
    ShapeStepData,
    SilentStepData,
    Step,
    StepSelections,
    StepType,
    TextboxElement,
    TextStepData,
    VariantResource,
    Workflow,
} from "../types";
import { CanvasCommand, CreateElementCommand } from "../command";
import { domParser, fetchAsString, toBase64, xmlSerializer } from "../util/crossplatform";
import { generate } from "../util/guid";
import { calculateOffsets, generateFrameSVG, getFrameData, getPatternImageData, getVariant } from "../util/frame";
import { sanitizeSvgTree, traverse } from "../util/illustration";
import { applyTextTransformations } from "../text/shared";
import { determineCorrectFontSizeAndLines } from "../text/algorithm/autosize";
import { loadFont } from "../util/font";

// SVG tags which can take a fill.
const fillableTagNames = [
    "altGlyph",
    "circle",
    "ellipse",
    "path",
    "polygon",
    "polyline",
    "rect",
    "text",
    "textPath",
    "tref",
    "tspan",
];

const handleDigitalContentStep = async (
    layouts: ILayout[],
    step: DesignInputStep,
    digitalContentStep: Step<DigitalContentStepData>,
): Promise<CreateElementCommand<ImageElement>[]> => {
    const designInputStepData = step.data as DigitalContentDesignInputStepData;
    const baseUrl = digitalContentStep.data.baseUrl;
    const videoSource = designInputStepData.assetUrl.replace("localhost", "localstack");
    const protocolPrefix = baseUrl.slice(0, 4) === "http" ? "" : "https://";
    const urlBuilder = new URL(protocolPrefix + baseUrl);
    urlBuilder.searchParams.append("video", toBase64(JSON.stringify([{ href: videoSource }])));
    //iOS does not recognise a URL without the trailing slash on the pathname,
    urlBuilder.pathname = urlBuilder.pathname + (urlBuilder.pathname.slice(-1) === "/" ? "" : "/");
    const finalUrl = urlBuilder.toString();
    const base64data = toBase64(await urlToQrString(finalUrl, { type: "svg" }));
    const dataUri = `data:image/svg+xml;base64,${base64data}`;

    const createElementForRegion = (region: Region): CreateElementCommand<ImageElement> => {
        const layout = layouts.find((layout: ILayout) => layout.panelId === region.panelId);
        if (!layout) {
            throw new LayoutNotFoundError(region);
        }

        // Create a new image.
        const elementKey = generate();
        return new CreateElementCommand<ImageElement>(
            {
                id: elementKey,
                src: dataUri,
                type: LayoutElementType.Image,
                y: region.top,
                x: region.left,
                width: region.width,
                height: region.height,
                rotation: 0,
            },
            layout,
        );
    };
    const regions = digitalContentStep.data.regions;
    try {
        return regions.map(createElementForRegion);
    } catch (e) {
        console.error(e);
        return [];
    }
};

const handleFrameStep = async (
    layouts: ILayout[],
    stepSelections: StepSelections,
    step: DesignInputStep,
    frameStep: Step<FrameStepData>,
): Promise<CanvasCommand[]> => {
    const designInputStepData = step.data as FrameDesignInputStepData;

    const src = designInputStepData.assetUrl;
    const imageData = await getPatternImageData(src);

    try {
        const variant = getVariant(designInputStepData, frameStep.option);
        if (variant) {
            stepSelections[frameStep.stepName] = { selectedVariants: [variant] };
        }

        const createFrameAndPattern = async (region: Region): Promise<CanvasCommand[]> => {
            const frameSvg = await generateFrameSVG(region, variant?.asset?.fileLink);
            const frameData = await getFrameData(frameSvg);
            const newElementId = generate();
            const layout = layouts.find((layout: ILayout) => layout.panelId === region.panelId);
            if (!layout) {
                throw new LayoutNotFoundError(region);
            }
            const offsets = calculateOffsets(
                imageData,
                frameData,
                {
                    scale: designInputStepData.scale,
                    left: designInputStepData.x,
                    top: designInputStepData.y,
                },
                frameStep.data.forceImageCover,
            );
            return [
                new CreateElementCommand<FrameElement>(
                    {
                        id: newElementId,
                        path: frameData.path,
                        type: LayoutElementType.Frame,
                        forceImageCover: frameStep.data.forceImageCover,
                        x: region.left,
                        y: region.top,
                        width: region.width,
                        height: region.height,
                        layer: region.layer,
                        layerIndex: region.layerIndex,
                        rotation: region.rotation,
                        scaleX: region.width / frameData.width,
                        scaleY: region.height / frameData.height,
                        pattern: {
                            id: `${newElementId}-pattern`,
                            src: imageData.src,
                            x: offsets.x,
                            y: offsets.y,
                            width: imageData.width,
                            height: imageData.height,
                            rotation: 0,
                            scaleX: offsets.zoom,
                            scaleY: offsets.zoom,
                            svg: imageData.svg,
                            colors: imageData.colors,
                        },
                    },
                    layout,
                ),
            ];
        };
        return (await Promise.all(frameStep.data.regions.map((r) => createFrameAndPattern(r)))).flat();
    } catch (e) {
        console.error(e);
        return [];
    }
};

const handleIllustrationStep = async (
    layouts: ILayout[],
    stepSelections: StepSelections,
    step: DesignInputStep,
    illustrationStep: Step<IllustrationStepData>,
): Promise<CreateElementCommand<IllustrationElement>[]> => {
    const designInputStepData = step.data as IllustrationDesignInputStepData;

    const option = illustrationStep.option;
    if (!option) {
        console.error(`No option for step ${illustrationStep.stepName}.`);
        return [];
    }
    const variant =
        option.variants?.find((variant: VariantResource) => variant.id === designInputStepData.illustrationVariantId) ||
        optionService.getDefaultVariant(option, illustrationStep.overrideDefaultVariantId);
    if (!variant) {
        console.error(`No variant with ID: ${designInputStepData.illustrationVariantId}`);
        return [];
    }
    if (!variant.asset) {
        console.error(`No asset for variant with ID: ${designInputStepData.illustrationVariantId}`);
        return [];
    }
    stepSelections[illustrationStep.stepName] = { selectedVariants: [variant] };

    const link = variant.asset?.fileLink;
    if (!link) {
        console.error(`No asset link for variant with ID: ${designInputStepData.illustrationVariantId}`);
        return [];
    }
    const illustrationBody = await fetchAsString(link, true);

    // Get the raw SVG without a doctype.
    const svgRegex = /<svg.*?<\/svg>/s;
    const svgMatches = illustrationBody.match(svgRegex) || [];
    const svg = svgMatches?.length > 0 ? svgMatches[0] : "";

    // Parse the SVG into a tree.
    const parser = domParser();
    const parsedSvg = parser.parseFromString(svg, "image/svg+xml");
    const root = parsedSvg.firstElementChild;
    if (!root) {
        console.error("Failed to read SVG.");
        return [];
    }
    sanitizeSvgTree(root);

    // Annotate elements which have fills with a new class name.
    const colors: { [key: string]: ColorDefinition } = {};
    traverse(root, (node) => {
        if (fillableTagNames.includes(node.tagName) && !node.attributes.getNamedItem("fill")) {
            node.setAttribute("fill", "#000000");
        }

        const fill = node.attributes.getNamedItem("fill");
        if (fill && fill.value !== "none") {
            const fillName = fill.value;
            const alphanumericFill = fillName.replace(/\W/g, "");
            const className = `spiff-fill-${alphanumericFill}`;
            node.classList.add(className);
            colors[className] = { browserValue: fillName };
        }

        const stroke = node.attributes.getNamedItem("stroke");
        if (stroke && stroke.value !== "none") {
            const fillName = stroke.value;
            const alphanumericFill = fillName.replace(/\W/g, "");
            const className = `spiff-stroke-${alphanumericFill}`;
            node.classList.add(className);
            colors[className] = { browserValue: fillName };
        }
    });

    // Serialise the traversed SVG back into a string.
    const serialiser = xmlSerializer();
    const rebuiltSvg = serialiser.serializeToString(root);

    const replacementColors = designInputStepData.colors;
    if (replacementColors) {
        for (const [className, colorDefinition] of Object.entries(colors)) {
            for (const k of Object.keys(replacementColors)) {
                if (colorDefinition.browserValue === k) {
                    colors[className] = { browserValue: replacementColors[k] };
                    break;
                }
            }
        }
    }

    const createElementForRegion = (region: Region): CreateElementCommand<IllustrationElement> => {
        const layout = layouts.find((layout: ILayout) => layout.panelId === region.panelId);
        if (!layout) {
            throw new LayoutNotFoundError(region);
        }
        const elementKey = generate();
        return new CreateElementCommand<IllustrationElement>(
            {
                colors,
                id: elementKey,
                svg: rebuiltSvg,
                type: LayoutElementType.Illustration,
                y: region.top,
                x: region.left,
                rotation: region.rotation,
                width: region.width,
                height: region.height,
                layer: region.layer,
                layerIndex: region.layerIndex,
                immutable: region.immutable,
            },
            layout,
        );
    };
    const regions = illustrationStep.data.regions;
    try {
        return regions.map(createElementForRegion);
    } catch (e) {
        console.error(e);
        return [];
    }
};

const handleModuleStep = async (
    layouts: ILayout[],
    step: DesignInputStep,
    moduleStep: Step<ModuleStepData>,
): Promise<CreateElementCommand<IllustrationElement>[]> => {
    const module = await moduleResolver(moduleStep.data.module);
    const designInputStepData = step.data as ModuleDesignInputStepData;
    const createElementForRegion = (svg: string, region: Region): CreateElementCommand<IllustrationElement> => {
        const layout = layouts.find((layout: ILayout) => layout.panelId === region.panelId);
        if (!layout) {
            throw new LayoutNotFoundError(region);
        }
        const elementKey = generate();
        return new CreateElementCommand<IllustrationElement>(
            {
                colors: {},
                id: elementKey,
                svg: svg,
                type: LayoutElementType.Illustration,
                y: region.top,
                x: region.left,
                rotation: region.rotation,
                width: region.width,
                height: region.height,
                layer: region.layer,
                layerIndex: region.layerIndex,
            },
            layout,
        );
    };
    const regions = moduleStep.data.regions;
    try {
        return regions.map((region) =>
            createElementForRegion(module.svgPrint(designInputStepData.text, region), region),
        );
    } catch (e) {
        console.error(e);
        return [];
    }
};

const handlePictureStep = async (
    layouts: ILayout[],
    stepSelections: StepSelections,
    step: DesignInputStep,
    pictureStep: Step<PictureStepData>,
): Promise<CreateElementCommand<ImageElement>[]> => {
    const designInputStepData = step.data as PictureDesignInputStepData;

    const option = pictureStep.option;
    if (!option) {
        console.error(`No option for step ${pictureStep.stepName}.`);
        return [];
    }
    const variant =
        option.variants?.find((variant: VariantResource) => variant.id === designInputStepData.pictureVariantId) ||
        optionService.getDefaultVariant(option, pictureStep.overrideDefaultVariantId);
    if (!variant) {
        console.error(`No variant with ID: ${designInputStepData.pictureVariantId}`);
        return [];
    }
    if (!variant.asset) {
        console.error(`No asset for variant with ID: ${designInputStepData.pictureVariantId}`);
        return [];
    }
    stepSelections[pictureStep.stepName] = { selectedVariants: [variant] };
    const link = variant.asset?.fileLink;
    if (!link) {
        console.error(`No asset link for variant with ID: ${designInputStepData.pictureVariantId}`);
        return [];
    }

    const createElementForRegion = (region: Region): CreateElementCommand<ImageElement> => {
        const layout = layouts.find((layout: ILayout) => layout.panelId === region.panelId);
        if (!layout) {
            throw new LayoutNotFoundError(region);
        }
        // Create a new image.
        const elementKey = generate();
        return new CreateElementCommand<ImageElement>(
            {
                id: elementKey,
                src: link,
                type: LayoutElementType.Image,
                y: region.top,
                x: region.left,
                rotation: region.rotation,
                width: region.width,
                height: region.height,
                layer: region.layer,
                layerIndex: region.layerIndex,
                immutable: region.immutable,
                preserveAspectRatio: "none",
            },
            layout,
        );
    };

    const regions = pictureStep.data.regions;
    try {
        return regions.map(createElementForRegion);
    } catch (e) {
        console.error(e);
        return [];
    }
};

const handleShapeStep = async (
    layouts: ILayout[],
    stepSelections: StepSelections,
    step: DesignInputStep,
    shapeStep: Step<ShapeStepData>,
): Promise<CreateElementCommand<IllustrationElement>[]> => {
    const designInputStepData = step.data as ShapeDesignInputStepData;
    const option = shapeStep.option;
    if (!option) {
        console.error(`No option for step ${shapeStep.stepName}.`);
        return [];
    }
    const variant =
        option.variants?.find((variant: VariantResource) => variant.id === designInputStepData.colorVariantId) ||
        optionService.getDefaultVariant(option, shapeStep.overrideDefaultVariantId);
    if (!variant) {
        console.error(`No variant with ID: ${designInputStepData.colorVariantId}`);
        return [];
    }
    stepSelections[shapeStep.stepName] = { selectedVariants: [variant] };

    const createElementForRegion = (region: Region): CreateElementCommand<IllustrationElement> => {
        const layout = layouts.find((layout: ILayout) => layout.panelId === region.panelId);
        if (!layout) {
            throw new LayoutNotFoundError(region);
        }

        const svg = `
        <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            xmlSpace="preserve"
            preserveAspectRatio="none"
            version="1.1"
            width="1"
            height="1"
            viewBox="0 0 1 1"
        >
            <rect
                x="0"
                y="0"
                width="1"
                height="1"
                class="spiff-fill-shape"
                fill="${variant?.color}"
            />
        </svg>
        `;
        const fill: ColorDefinition = { browserValue: variant.color || "#000000" };
        const colors = { "spiff-fill-shape": fill };

        const elementKey = generate();
        return new CreateElementCommand<IllustrationElement>(
            {
                colors,
                id: elementKey,
                svg,
                type: LayoutElementType.Illustration,
                y: region.top,
                x: region.left,
                rotation: region.rotation,
                width: region.width,
                height: region.height,
                layer: region.layer,
                layerIndex: region.layerIndex,
                immutable: region.immutable,
            },
            layout,
        );
    };
    const regions = shapeStep.data.regions;
    try {
        return regions.map(createElementForRegion);
    } catch (e) {
        console.error(e);
        return [];
    }
};

const handleSilentStep = async (
    layouts: ILayout[],
    silentStep: Step<SilentStepData>,
    productOverlayImageUrl?: string,
): Promise<CreateElementCommand<LayoutElement>[]> => {
    const commands: CreateElementCommand<LayoutElement>[] = [];

    const getSource = (): string => {
        if (silentStep.type === StepType.ProductOverlay) {
            return productOverlayImageUrl || silentStep.data.asset?.fileLink || "";
        } else {
            return silentStep.data.asset?.fileLink || "";
        }
    };
    const assetSource = getSource();

    const evaluateAssetType = (): LayoutElementType => {
        // Try to determine the type by asset path.
        if (assetSource.endsWith(".jpeg") || assetSource.endsWith(".jpg") || assetSource.endsWith(".png")) {
            return LayoutElementType.Image;
        }
        if (assetSource.endsWith(".svg")) {
            return LayoutElementType.Illustration;
        }
        return LayoutElementType.Illustration;
    };
    const assetType = evaluateAssetType();
    const regions = silentStep.data.regions;

    if (assetType === "image") {
        try {
            regions.forEach((region) => {
                const layout = layouts.find((layout: ILayout) => layout.panelId === region.panelId);
                if (!layout) {
                    throw new LayoutNotFoundError(region);
                }
                const newElement = {
                    id: generate(),
                    src: assetSource,
                    type: assetType,
                    y: region.top,
                    x: region.left,
                    width: region.width,
                    height: region.height,
                    layer: region.layer,
                    layerIndex: region.layerIndex,
                    productOverlay: silentStep.type === StepType.ProductOverlay ? true : undefined,
                    scaleX: 1,
                    scaleY: 1,
                    rotation: region.rotation,
                    excludeFromExport: silentStep.data.excludeFromPrint,
                    preserveAspectRatio: "none",
                };
                commands.push(new CreateElementCommand<ImageElement>(newElement, layout));
            });
        } catch (e) {
            console.error(e);
        }
    } else {
        const fetchedSrc = await fetchAsString(assetSource, true);
        const sanitizeSvg = (rawSvg: string): string => {
            // Get the raw SVG without a doctype.
            const svgRegex = /<svg.*?<\/svg>/s;
            const svgMatches = rawSvg.match(svgRegex) || [];
            const svg = svgMatches?.length > 0 ? svgMatches[0] : "";

            // Parse the SVG into a tree.
            const parser = domParser();
            const parsedSvg = parser.parseFromString(svg, "image/svg+xml");
            const root = parsedSvg.firstElementChild;
            if (!root) {
                throw new ParseError("Failed to read SVG.");
            }
            sanitizeSvgTree(root);
            const serialiser = xmlSerializer();
            return serialiser.serializeToString(root);
        };
        regions.forEach((region) => {
            const layout = layouts.find((layout: ILayout) => layout.panelId === region.panelId);
            if (!layout) {
                throw new LayoutNotFoundError(region);
            }
            const newElement = {
                id: generate(),
                src: assetSource,
                asset_key: assetSource,
                svg: sanitizeSvg(fetchedSrc),
                colors: {},
                type: assetType,
                y: region.top,
                x: region.left,
                width: region.width,
                height: region.height,
                layer: region.layer,
                layerIndex: region.layerIndex,
                scaleX: 1,
                scaleY: 1,
                rotation: region.rotation,
                productOverlay: silentStep.type === StepType.ProductOverlay ? true : undefined,
                excludeFromExport: silentStep.data.excludeFromPrint,
            };
            commands.push(new CreateElementCommand<IllustrationElement>(newElement, layout));
        });
    }
    return commands;
};

const handleTextStep = async (
    layouts: ILayout[],
    stepSelections: StepSelections,
    step: DesignInputStep,
    textStep: Step<TextStepData>,
): Promise<CreateElementCommand<TextboxElement>[]> => {
    const commands: CreateElementCommand<TextboxElement>[] = [];

    const designInputStepData = step.data as TextDesignInputStepData;

    const defaultFontSize = 30;
    const textAlign = (stepData: TextStepData) => {
        if (stepData.vertical) {
            return "center";
        }
        return stepData.textAlign || "center";
    };

    const getFontUrl = (): string | undefined => {
        const option = textStep.option;
        if (!option) {
            return undefined;
        }
        const variant =
            option.variants?.find((variant: VariantResource) => variant.id === designInputStepData.fontVariantId) ||
            optionService.getDefaultVariant(option, textStep.overrideDefaultVariantId);
        if (!variant) {
            return undefined;
        }
        if (!variant.asset) {
            return undefined;
        }
        stepSelections[textStep.stepName] = { selectedVariants: [variant] };
        const link = variant.asset.fileLink;
        if (!link) {
            return undefined;
        }
        return link;
    };

    const getFontData = async () => {
        const fontUrl = getFontUrl();
        if (!fontUrl) {
            return undefined;
        }
        const font = await loadFont(fontUrl);
        const fontData: FontData = {
            assetUrl: fontUrl,
            name: font.names.fullName["en"],
        };
        return fontData;
    };
    const fontData = await getFontData();

    const replacedText =
        (textStep.data.replaceableText
            ? textStep.data.replaceableText.replace("{{}}", designInputStepData.text)
            : designInputStepData.text) || "";

    const text = applyTextTransformations(replacedText, {
        vertical: textStep.data.vertical,
        uppercase: textStep.data.uppercase,
    });

    const getDefaultColor = async (step: TextStepData): Promise<string | undefined> => {
        const colorOption = step.colorOption;
        if (!colorOption) return undefined;
        const defaultVariant = optionService.getDefaultVariant(colorOption);
        return defaultVariant?.color;
    };
    const defaultColor = designInputStepData.color || (await getDefaultColor(textStep.data));

    const regions = textStep.data.regions;
    for (const region of regions) {
        const layout = layouts.find((layout) => layout.panelId === region.panelId);
        if (!layout) {
            continue;
        }
        const newBaseElement: TextboxElement = {
            stepName: step.name,
            id: generate(),
            align: textAlign(textStep.data),
            curved: textStep.data.curved,
            fill: designInputStepData.color || defaultColor || "#000000",
            fontData,
            fontSize: textStep.data.size || defaultFontSize,
            height: region.height,
            layer: region.layer,
            layerIndex: region.layerIndex,
            paths: textStep.data.paths,
            rotation: region.rotation,
            text,
            type: LayoutElementType.Textbox,
            vertical: textStep.data.vertical,
            verticalAlign: textStep.data.verticalAlign || "middle",
            width: region.width,
            x: region.left,
            y: region.top,
        };
        if (fontData) {
            const [correctFontSize, correctLines] = determineCorrectFontSizeAndLines(
                textStep.data.size || defaultFontSize,
                fontData,
                region,
                [
                    applyTextTransformations(text, {
                        vertical: textStep.data.vertical,
                        uppercase: textStep.data.uppercase,
                    }),
                ],
                {
                    size: textStep.data.size,
                    minSize: textStep.data.minSize,
                    maxSize: textStep.data.maxSize,
                },
            );
            commands.push(
                new CreateElementCommand(
                    {
                        ...newBaseElement,
                        fontSize: correctFontSize,
                        text: newBaseElement.curved ? newBaseElement.text : (correctLines || []).join("\n"),
                    },
                    layout,
                ),
            );
        } else {
            commands.push(new CreateElementCommand(newBaseElement, layout));
        }
    }
    return commands;
};

// Whether all of a step's conditions have been met.
const stepConditionsAreSatisfied = (step: Step<AnyStepData>, stepSelections: StepSelections): boolean => {
    if (!step.conditions) {
        return true;
    }
    return step.conditions.every((condition) => {
        const originatingStepData = stepSelections[condition.targetStepName];
        if (originatingStepData && originatingStepData.selectedVariants) {
            const selectedVariants = originatingStepData.selectedVariants;
            return condition.requiredVariantSelections.some(
                (reqVariant) => selectedVariants.find((selVariant) => selVariant.id === reqVariant) !== undefined,
            );
        }
        return false;
    });
};

// Generate a set of commands corresponding to one workflow.
const generateCommands = async (
    designInputSteps: DesignInputStep[],
    workflow: Workflow,
    layouts: ILayout[],
    productOverlayImageUrl?: string,
): Promise<CanvasCommand[]> => {
    const commands: CanvasCommand[] = [];
    const stepSelections = {};

    // Apply steps.
    for (const step of designInputSteps) {
        const workflowStep = workflow.steps.find((s) => s.stepName === step.name);
        if (!workflowStep) {
            continue;
        }
        switch (workflowStep.type) {
            case StepType.DigitalContent:
                commands.push(
                    ...(await handleDigitalContentStep(layouts, step, workflowStep as Step<DigitalContentStepData>)),
                );
                break;
            case StepType.Frame:
            case StepType.Photo:
                commands.push(
                    ...(await handleFrameStep(layouts, stepSelections, step, workflowStep as Step<FrameStepData>)),
                );
                break;
            case StepType.Illustration:
                commands.push(
                    ...(await handleIllustrationStep(
                        layouts,
                        stepSelections,
                        step,
                        workflowStep as Step<IllustrationStepData>,
                    )),
                );
                break;
            case StepType.Module:
                commands.push(...(await handleModuleStep(layouts, step, workflowStep as Step<ModuleStepData>)));
                break;
            case StepType.Picture:
                commands.push(
                    ...(await handlePictureStep(layouts, stepSelections, step, workflowStep as Step<PictureStepData>)),
                );
                break;
            case StepType.Shape:
                commands.push(
                    ...(await handleShapeStep(layouts, stepSelections, step, workflowStep as Step<ShapeStepData>)),
                );
                break;
            case StepType.Text:
                commands.push(
                    ...(await handleTextStep(layouts, stepSelections, step, workflowStep as Step<TextStepData>)),
                );
                break;
        }
    }

    // Handle silent steps.
    for (const workflowStep of workflow.steps) {
        if (workflowStep.type !== StepType.SilentIllustration && workflowStep.type !== StepType.ProductOverlay) {
            continue;
        }
        if (!stepConditionsAreSatisfied(workflowStep, stepSelections)) {
            continue;
        }
        commands.push(
            ...(await handleSilentStep(layouts, workflowStep as Step<SilentStepData>, productOverlayImageUrl)),
        );
    }

    return commands;
};

export { generateCommands };
